import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor } from '@splashthat/splash-cms-engine';
import {
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_SUBHEADLINE_TEXT,
    BRAND_LIBRARY_HEADLINE_TEXT,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { mockSessionRegistration } from 'common/components/TouchpointEditor/mockSessionsData';
import { SessionsSummaryWrapper, HeadlineContent } from './SessionsSummary.styles';
import { Sessions } from './Sessions';

export const defaultProps = {
    styles: {},
    meta: {
        visible: true,
        displayName: 'Session Summary',
    },
    descendants: {
        headline: {
            styles: {
                fontSize: 16,
                fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
            },
            meta: {
                content: {
                    sessionsRegisteredContent: 'Confirmed Sessions:',
                    unConfirmedSessionsContent: 'Unconfirmed Sessions:',
                    openSessionText: 'Open Session | ',
                    waitlistText: 'Waitlist | ',
                    addToCalendarContent: 'Add To Calendar: ',
                },
            },
        },
        sessionTitle: {
            styles: {
                fontSize: 14,
                fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                fontFamily: BRAND_LIBRARY_SUBHEADLINE_TEXT,
            },
        },
        sessionInformation: {
            styles: {
                fontSize: 14,
                fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                fontFamily: BRAND_LIBRARY_BODY_TEXT,
            },
        },
        AddToCalendar: {
            meta: {
                visible: true,
            },
            styles: {},
            descendants: {
                modal: {
                    styles: {
                        backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    },
                },
                headline: {
                    styles: {},
                    meta: {
                        content: null,
                    },
                },
                providerLogoIcons: {
                    styles: {
                        color: null,
                        colorHover: null,
                    },
                },
                providerLogoNames: {
                    styles: {},
                },
            },
        },
    },
};

const SessionsSummary = React.forwardRef(
    (
        {
            meta: { visible },
            descendants: { headline, sessionTitle, sessionInformation, AddToCalendar },
            hovered,
            selected,
        },
        ref,
    ) => {
        const { event, theme } = useTouchpointData();

        const node = useNode();
        const editor = useEditor((state) => ({
            editing: state.editing,
        }));
        const createSetContent = useCallback(
            (contentKey) => (text) =>
                node.actions.setProp((editedNode) => {
                    editedNode.descendants.headline.meta.content[contentKey] = text; // eslint-disable-line no-param-reassign
                    return editedNode;
                }),
            [],
        );

        const setSessionsRegisteredContent = createSetContent('sessionsRegisteredContent');
        const setUnconfirmedSessionsContent = createSetContent('unConfirmedSessionsContent');
        const setAddToCalendarContent = createSetContent('addToCalendarContent');
        const setOpenSessionText = createSetContent('openSessionText');
        const setWaitlistText = createSetContent('waitlistText');

        const sessionsRegistration = editor.editing ? mockSessionRegistration : event.sessionRegistrationConfirmation;
        const { sessionsRegistered = [], sessionsSkipped = [], sessionsWaitlisted = [] } = sessionsRegistration ?? {};
        return visible && (sessionsRegistered?.length || sessionsSkipped?.length || sessionsWaitlisted?.length) ? (
            <SessionsSummaryWrapper
                selected={selected}
                hovered={hovered}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                onClick={node.actions.onClick()}
                ref={ref}
            >
                {sessionsRegistered?.length ? (
                    <>
                        <HeadlineContent
                            $fontColor={theme.colors[headline.styles.fontColor]}
                            $fontFamily={theme.activeFonts[headline.styles.fontFamily]}
                            $fontSize={headline.styles.fontSize}
                        >
                            <TextEditable
                                setContent={setSessionsRegisteredContent}
                                disabled={!editor.editing || (!selected && !hovered)}
                            >
                                {headline.meta.content.sessionsRegisteredContent}
                            </TextEditable>
                        </HeadlineContent>

                        <Sessions
                            sessionsDetails={sessionsRegistered}
                            sessionTitle={sessionTitle}
                            sessionInformation={sessionInformation}
                            setTitlePrefixText={setOpenSessionText}
                            titlePrefixText={headline.meta.content.openSessionText}
                            setAddToCalendarContent={setAddToCalendarContent}
                            addToCalendarContent={headline.meta.content.addToCalendarContent}
                            addToCalendar={AddToCalendar}
                            selected={selected}
                            hovered={hovered}
                            isCalendarVisible={AddToCalendar.meta.visible}
                        />
                    </>
                ) : null}
                {sessionsWaitlisted?.length || sessionsSkipped?.length ? (
                    <>
                        <HeadlineContent
                            $fontColor={theme.colors[headline.styles.fontColor]}
                            $fontFamily={theme.activeFonts[headline.styles.fontFamily]}
                            $fontSize={headline.styles.fontSize}
                        >
                            <TextEditable
                                setContent={setUnconfirmedSessionsContent}
                                disabled={!editor.editing || (!selected && !hovered)}
                            >
                                {headline.meta.content.unConfirmedSessionsContent}
                            </TextEditable>
                        </HeadlineContent>
                        {sessionsWaitlisted?.length ? (
                            <Sessions
                                sessionsDetails={sessionsWaitlisted}
                                headline={headline}
                                sessionTitle={sessionTitle}
                                sessionInformation={sessionInformation}
                                setTitlePrefixText={setWaitlistText}
                                titlePrefixText={headline.meta.content.waitlistText}
                                selected={selected}
                                hovered={hovered}
                                isCalendarVisible={false}
                                isSessionWaitlisted
                            />
                        ) : null}
                        {sessionsSkipped?.length ? (
                            <Sessions
                                sessionsDetails={sessionsSkipped}
                                headline={headline}
                                sessionTitle={sessionTitle}
                                sessionInformation={sessionInformation}
                                openSessionText={headline.meta.content.openSessionText}
                                selected={selected}
                                hovered={hovered}
                                isCalendarVisible={false}
                            />
                        ) : null}
                    </>
                ) : null}
            </SessionsSummaryWrapper>
        ) : null;
    },
);

SessionsSummary.splash = {
    related: {
        settings: React.lazy(() => import('./SessionsSummary.tools')),
    },
    defaultProps,
};

SessionsSummary.propTypes = {
    meta: PropTypes.shape({
        content: PropTypes.string,
        visible: PropTypes.bool,
    }).isRequired,
    descendants: PropTypes.object.isRequired,
    hovered: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
};

export default SessionsSummary;
